import WebGPURenderer, { RenderableInterface } from "~/src/WebGPURenderer";
import drawShader from "bundle-text:./draw-shader.wgsl";
import { mat4, vec3 } from "gl-matrix";
import { primitives } from "twgl.js";
const DEG_TO_RAD = 0.0174533;

const EYE = vec3.fromValues(0, 0, -1.115);
const TARGET = vec3.fromValues(0, 0, 0);
const UP = vec3.fromValues(0, 1, 0);

class ParticlesRenderable implements RenderableInterface {
  private pipeline: GPURenderPipeline;
  private simulationBindGroup?: GPUBindGroup;
  private vertexPositionBuffer: GPUBuffer;
  private vertexNormalBuffer: GPUBuffer;
  private vertexTexCoordBuffer: GPUBuffer;
  private indicesBuffer: GPUBuffer;
  private vertexCount: number;
  private uniformsBindGroupLayout: GPUBindGroupLayout;
  private uniformsBindGroup: GPUBindGroup;

  constructor(
    private device: GPUDevice,
    private renderer: WebGPURenderer,
    private particlesCount: number,
    private sharedUniformsBindGroupLayout: GPUBindGroupLayout,
    private sharedUniformsBindGroup: GPUBindGroup,
    private simulationBindGroupLayout: GPUBindGroupLayout,
    private texture: GPUTexture
  ) {
    this.createVertexBuffer();
    this.createViewProjectionMatrix();
    this.createUniformsBuffersAndBindGroups();
    this.createPipeline();
  }

  private createViewProjectionMatrix(): Float32Array {
    const aspect = this.renderer.width / this.renderer.height;
    const projectionMatrix = mat4.perspective(
      mat4.create(),
      DEG_TO_RAD * 40,
      aspect,
      0.01,
      9999
    );

    const camera = mat4.lookAt(mat4.create(), EYE, TARGET, UP);
    const viewMatrix = mat4.invert(mat4.create(), camera);

    const viewProjectionMatrix = mat4.multiply(
      mat4.create(),
      projectionMatrix,
      viewMatrix
    );

    return viewProjectionMatrix as Float32Array;
  }

  private createModelMatrix(): Float32Array {
    const modelMatrix = mat4.identity(mat4.create());
    return modelMatrix as Float32Array;
  }

  private createVertexBuffer() {
    const { indices, normal, position, texcoord } =
      primitives.createSphereVertices(1, 64, 32);

    this.vertexCount = indices.length;

    this.vertexPositionBuffer = this.device.createBuffer({
      size: position.byteLength,
      usage: GPUBufferUsage.VERTEX,
      mappedAtCreation: true,
    });
    new Float32Array(this.vertexPositionBuffer.getMappedRange()).set(position);
    this.vertexPositionBuffer.unmap();

    this.vertexNormalBuffer = this.device.createBuffer({
      size: normal.byteLength,
      usage: GPUBufferUsage.VERTEX,
      mappedAtCreation: true,
    });
    new Float32Array(this.vertexNormalBuffer.getMappedRange()).set(normal);
    this.vertexNormalBuffer.unmap();

    this.vertexTexCoordBuffer = this.device.createBuffer({
      size: texcoord.byteLength,
      usage: GPUBufferUsage.VERTEX,
      mappedAtCreation: true,
    });
    new Float32Array(this.vertexTexCoordBuffer.getMappedRange()).set(texcoord);
    this.vertexTexCoordBuffer.unmap();

    this.indicesBuffer = this.device.createBuffer({
      size: indices.byteLength,
      usage: GPUBufferUsage.INDEX,
      mappedAtCreation: true,
    });
    new Uint16Array(this.indicesBuffer.getMappedRange()).set(indices);
    this.indicesBuffer.unmap();
  }

  private createUniformsBuffersAndBindGroups() {
    var viewProjectionMatrix = this.createViewProjectionMatrix();
    var modelMatrix = this.createModelMatrix();
    const matrixUniformBuffer = this.device.createBuffer({
      size: viewProjectionMatrix.byteLength + modelMatrix.byteLength,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    var data = new Float32Array(
      viewProjectionMatrix.length + modelMatrix.length
    );
    data.set(viewProjectionMatrix);
    data.set(modelMatrix, viewProjectionMatrix.length);

    this.device.queue.writeBuffer(matrixUniformBuffer, 0, data);

    this.uniformsBindGroupLayout = this.device.createBindGroupLayout({
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility:
            GPUShaderStage.VERTEX |
            GPUShaderStage.FRAGMENT |
            GPUShaderStage.COMPUTE,
          sampler: { type: "filtering" },
        },
        {
          binding: 2,
          visibility:
            GPUShaderStage.VERTEX |
            GPUShaderStage.FRAGMENT |
            GPUShaderStage.COMPUTE,
          texture: {
            sampleType: "float",
            multisampled: false,
            viewDimension: "2d",
          },
        },
      ],
    });

    this.uniformsBindGroup = this.device.createBindGroup({
      layout: this.uniformsBindGroupLayout,
      entries: [
        {
          binding: 0,
          resource: {
            buffer: matrixUniformBuffer,
          },
        },
        {
          binding: 1,
          resource: this.device.createSampler({
            magFilter: "linear",
            minFilter: "linear",
          }),
        },
        {
          binding: 2,
          resource: this.texture.createView(),
        },
      ],
    });
  }

  private createPipeline() {
    const shaderModule = this.device.createShaderModule({ code: drawShader });

    const vertexState: GPUVertexState = {
      module: shaderModule,
      entryPoint: "vertex_main",
      buffers: [
        {
          arrayStride: 3 * Float32Array.BYTES_PER_ELEMENT,
          stepMode: "vertex",
          attributes: [
            {
              format: "float32x3" as GPUVertexFormat,
              offset: 0,
              shaderLocation: 0,
            },
          ],
        },
        {
          arrayStride: 3 * Float32Array.BYTES_PER_ELEMENT,
          stepMode: "vertex",
          attributes: [
            {
              format: "float32x3" as GPUVertexFormat,
              offset: 0,
              shaderLocation: 1,
            },
          ],
        },
        {
          arrayStride: 2 * Float32Array.BYTES_PER_ELEMENT,
          stepMode: "vertex",
          attributes: [
            {
              format: "float32x2" as GPUVertexFormat,
              offset: 0,
              shaderLocation: 2,
            },
          ],
        },
      ],
    };

    const fragmentState: GPUFragmentState = {
      module: shaderModule,
      entryPoint: "fragment_main",
      targets: [
        {
          format: this.renderer.presentationFormat,
          blend: {
            color: {
              srcFactor: "src-alpha",
              dstFactor: "one-minus-src-alpha",
              operation: "add",
            },
            alpha: {
              srcFactor: "src-alpha",
              dstFactor: "one-minus-src-alpha",
              operation: "add",
            },
          },
        },
      ],
    };

    const pipelineLayout = this.device.createPipelineLayout({
      bindGroupLayouts: [
        this.simulationBindGroupLayout,
        this.sharedUniformsBindGroupLayout,
        this.uniformsBindGroupLayout,
      ],
    });

    this.pipeline = this.device.createRenderPipeline({
      layout: pipelineLayout,
      vertex: vertexState,
      fragment: fragmentState,
      // primitive: {
      //   topology: "line-list",
      // },
      depthStencil: {
        format: this.renderer.depthFormat,
        depthWriteEnabled: true,
        depthCompare: "less",
      },
    });
  }

  public set simulationSrcBindGroup(group: GPUBindGroup | undefined) {
    this.simulationBindGroup = group;
  }

  public get simulationSrcBindGroup(): GPUBindGroup | undefined {
    return this.simulationBindGroup;
  }

  public getCommands(
    renderPassDescriptor: GPURenderPassDescriptor
  ): GPUCommandBuffer | undefined {
    if (!this.simulationSrcBindGroup) return;

    const commandEncoder = this.device.createCommandEncoder();
    const renderPass = commandEncoder.beginRenderPass(renderPassDescriptor);
    renderPass.setPipeline(this.pipeline);
    renderPass.setBindGroup(0, this.simulationSrcBindGroup);
    renderPass.setBindGroup(1, this.sharedUniformsBindGroup);
    renderPass.setBindGroup(2, this.uniformsBindGroup);
    renderPass.setVertexBuffer(0, this.vertexPositionBuffer);
    renderPass.setVertexBuffer(1, this.vertexNormalBuffer);
    renderPass.setVertexBuffer(2, this.vertexTexCoordBuffer);
    renderPass.setIndexBuffer(this.indicesBuffer, "uint16");
    renderPass.drawIndexed(this.vertexCount, this.particlesCount, 0, 0);
    renderPass.end();

    return commandEncoder.finish();
  }
}

export default ParticlesRenderable;
